import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { api, apiError } from "../lib/api";
import { Card, Btn, Input, Badge, Modal, Empty } from "../components/UI";
import { CocoaSelect } from "../components/CocoaSelect";
import { DateField } from "./engagement/DateField";
import { CalendarRange, Users, Pencil, Plus, X, Search, PhoneCall, Trash2, Repeat2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const mins = (t) => { const [h, m] = String(t || "").split(":"); return (+h || 0) * 60 + (+m || 0); };

export function OnCallCard({ users, isAdmin }) {
  const [data, setData] = useState(null);
  const [userId, setUserId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  const [rotOpen, setRotOpen] = useState(false);
  const [rotUsers, setRotUsers] = useState([]);
  const [rotStart, setRotStart] = useState("");
  const [rotLength, setRotLength] = useState("7");
  const [rotWeeks, setRotWeeks] = useState("8");
  const [rotBusy, setRotBusy] = useState(false);
  const load = () => api.get("/oncall").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const eligible = users.filter((u) => u.status === "active" && ["admin", "clinician"].includes(u.role));
  const add = async () => {
    if (!userId || !start || !end) return toast.error("Pick a clinician plus start and end dates");
    setBusy(true);
    try {
      await api.post("/oncall", { user_id: userId, start_date: start, end_date: end });
      toast.success("On-call coverage added");
      setUserId(""); setStart(""); setEnd("");
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/oncall/${id}`); toast.success("Roster entry removed"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleRotUser = (uid) => setRotUsers((s) => s.includes(uid) ? s.filter((x) => x !== uid) : [...s, uid]);
  const rotPreview = (() => {
    if (rotUsers.length < 2 || !rotStart) return [];
    const len = Math.max(1, Math.min(14, Number(rotLength) || 7));
    const start = dayjs(rotStart);
    if (!start.isValid()) return [];
    const shifts = 4;
    const userMap = Object.fromEntries(users.map((u) => [u.id, u]));
    return Array.from({ length: shifts }).map((_, i) => {
      const s = start.add(i * len, "day");
      const e = start.add((i + 1) * len - 1, "day");
      const u = userMap[rotUsers[i % rotUsers.length]] || {};
      return { idx: i + 1, start: s.format("MMM D"), end: e.format("MMM D"),
               user: `${u.first_name || "?"} ${u.last_name || ""}`.trim() };
    });
  })();
  const runRotation = async () => {
    if (rotUsers.length < 2) return toast.error("Pick at least two clinicians for a rotation");
    if (!rotStart) return toast.error("Choose a start date (any Monday works best)");
    setRotBusy(true);
    try {
      const r = await api.post("/oncall/rotation", { user_ids: rotUsers, start_date: rotStart,
        length_days: Number(rotLength) || 7, weeks: Number(rotWeeks) || 8 });
      toast.success(`Auto-rotate filled ${r.data.created} shifts`);
      setRotOpen(false); setRotUsers([]); setRotStart(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setRotBusy(false); }
  };
  if (!data) return null;
  const today = dayjs().format("YYYY-MM-DD");
  return (
    <Card className="mb-6 p-5" data-testid="oncall-card">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <PhoneCall size={15} className="text-[#7B6157]" />
        <h3 className="font-heading text-sm font-bold text-[#3E2723]">On-Call Roster</h3>
        {data.current ? (
          <span data-testid="oncall-current" className="rounded-full bg-[#E8F5E9] px-2.5 py-1 text-[11px] font-bold text-[#2E7D32]">Covering now: {data.current.user_name} (through {dayjs(data.current.end_date).format("MMM D")})</span>
        ) : (
          <span data-testid="oncall-current" className="rounded-full bg-[#FFF8E1] px-2.5 py-1 text-[11px] font-bold text-[#876600]">No on-call coverage set</span>
        )}
        {isAdmin && (
          <button data-testid="oncall-rotate-toggle" onClick={() => setRotOpen((v) => !v)}
            className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#E5DFD3] px-3 py-1 text-[11px] font-bold text-[#4E342E] hover:bg-[#F5F2EB]">
            <Repeat2 size={12} /> {rotOpen ? "Close auto-rotate" : "Auto-Rotate"}
          </button>
        )}
      </div>
      <p className="mb-3 text-xs text-[#7B6157]">High-risk C-SSRS alerts route to the on-call clinician first, plus the client's assigned clinician. Without coverage, alerts fall back to the assigned clinician only.</p>
      {isAdmin && rotOpen && (
        <div data-testid="oncall-rotate-panel" className="mb-4 rounded-2xl border border-[#EFEBE0] bg-[#FDFCF8] p-4">
          <div className="mb-2 flex items-center gap-2">
            <Repeat2 size={14} className="text-[#4E342E]" />
            <div className="font-heading text-sm font-bold text-[#3E2723]">Weekly Auto-Rotate</div>
          </div>
          <p className="mb-3 text-xs text-[#7B6157]">Pick two or more clinicians, a start date, and how many weeks to fill ahead. Cocoa creates non-overlapping shifts in the order you select, and each clinician gets one alert with their upcoming coverage.</p>
          <div className="mb-3 flex flex-wrap gap-1.5" data-testid="oncall-rotate-users">
            {eligible.map((u) => (
              <button key={u.id} data-testid={`oncall-rotate-user-${u.id}`} onClick={() => toggleRotUser(u.id)}
                className={`rounded-full px-3 py-1 text-[11px] font-bold border transition-colors ${
                  rotUsers.includes(u.id) ? "border-[#4E342E] bg-[#4E342E] text-white"
                  : "border-[#E5DFD3] bg-white text-[#4E342E] hover:bg-[#F5F2EB]"}`}>
                {rotUsers.includes(u.id) ? `${rotUsers.indexOf(u.id) + 1}. ${u.first_name} ${u.last_name}` : `${u.first_name} ${u.last_name}`}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Start date</div>
              <DateField testId="oncall-rotate-start" placeholder="Mon" className="w-36" value={rotStart} onChange={setRotStart} />
            </div>
            <div>
              <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Shift length (days)</div>
              <Input data-testid="oncall-rotate-length" type="number" min="1" max="14" value={rotLength}
                onChange={(e) => setRotLength(e.target.value)} className="w-24" />
            </div>
            <div>
              <div className="mb-0.5 text-[10px] font-bold uppercase text-[#7B6157]">Fill ahead (weeks)</div>
              <Input data-testid="oncall-rotate-weeks" type="number" min="1" max="26" value={rotWeeks}
                onChange={(e) => setRotWeeks(e.target.value)} className="w-24" />
            </div>
            <Btn data-testid="oncall-rotate-run" onClick={runRotation} disabled={rotBusy || rotUsers.length < 2 || !rotStart}>
              {rotBusy ? "Generating…" : "Generate Rotation"}
            </Btn>
          </div>
          {rotPreview.length > 0 && (
            <div className="mt-3 rounded-xl border border-[#E5DFD3] bg-white p-3" data-testid="oncall-rotate-preview">
              <div className="mb-2 text-[10px] font-bold uppercase text-[#7B6157]">Preview: next {rotPreview.length} shifts</div>
              <div className="space-y-1">
                {rotPreview.map((p) => (
                  <div key={p.idx} data-testid={`oncall-rotate-preview-${p.idx}`}
                    className="flex items-center gap-3 text-xs">
                    <span className="rounded-full bg-[#EDE7F6] px-2 py-0.5 font-bold text-[#5E35B1]">#{p.idx}</span>
                    <span className="font-semibold text-[#3E2723]">{p.user}</span>
                    <span className="text-[#7B6157]">{p.start} - {p.end}</span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] text-[#7B6157]">
                Cocoa will create {Math.max(1, Math.ceil((Number(rotWeeks) || 8) * 7 / (Number(rotLength) || 7)))} shifts total in this rotation.
              </div>
            </div>
          )}
        </div>
      )}
      {isAdmin && (
        <div className="mb-4 flex flex-wrap items-end gap-2">
          <CocoaSelect testId="oncall-user-select" className="w-52" placeholder="Select clinician…" value={userId} onChange={setUserId}
            options={eligible.map((u) => [u.id, `${u.first_name} ${u.last_name}`])} />
          <DateField testId="oncall-start" placeholder="Start" className="w-36" value={start} disabledAfter={end || undefined} onChange={setStart} />
          <DateField testId="oncall-end" placeholder="End" className="w-36" value={end} disabledBefore={start || undefined} onChange={setEnd} />
          <Btn data-testid="oncall-add-btn" onClick={add} disabled={busy}><Plus size={14} /> Add Coverage</Btn>
        </div>
      )}
      {data.entries.length === 0 ? <Empty text="No roster entries yet" /> : (
        <div className="space-y-1.5">
          {data.entries.slice(0, 8).map((e) => {
            const active = e.start_date <= today && today <= e.end_date;
            const past = e.end_date < today;
            return (
              <div key={e.id} data-testid={`oncall-row-${e.id}`} className={`flex flex-wrap items-center gap-3 rounded-xl border px-4 py-2 text-sm ${active ? "border-[#C8E6C9] bg-[#F1F8E9]" : "border-[#F1EEE7]"} ${past ? "opacity-60" : ""}`}>
                <span className="font-semibold text-[#3E2723]">{e.user_name}</span>
                <span className="text-xs text-[#7B6157]">{dayjs(e.start_date).format("MMM D")} - {dayjs(e.end_date).format("MMM D, YYYY")}</span>
                {active && <span className="rounded-full bg-[#2E7D32] px-2 py-0.5 text-[10px] font-bold text-white">ACTIVE</span>}
                {e.rotation_id && <span className="rounded-full bg-[#EDE7F6] px-2 py-0.5 text-[10px] font-bold text-[#5E35B1]">AUTO-ROTATE</span>}
                {isAdmin && (
                  <button data-testid={`oncall-delete-${e.id}`} onClick={() => remove(e.id)} className="ml-auto rounded-lg p-1.5 text-[#C62828] hover:bg-[#FFEBEE]"><Trash2 size={13} /></button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export function WorkScheduleCard({ userId, canEdit }) {
  const [sched, setSched] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/users/${userId}/schedule`).then((r) => setSched(r.data)).catch(() => {}); }, [userId]);
  if (!sched) return <Card className="p-5 animate-pulse h-40" />;

  const weeklyHours = DAYS.reduce((sum, [d]) => {
    const e = sched.days?.[d];
    return e?.enabled ? sum + Math.max(mins(e.end) - mins(e.start), 0) / 60 : sum;
  }, 0);

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put(`/users/${userId}/schedule`, draft);
      setSched(r.data); setEditing(false); toast.success("Work schedule saved");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const setDay = (d, patch) => setDraft((s) => ({ ...s, days: { ...s.days, [d]: { ...s.days[d], ...patch } } }));

  return (
    <Card className="p-5" data-testid="ws-card">
      <div className="mb-3 flex items-center gap-2">
        <CalendarRange size={15} className="text-[#7B6157]" />
        <h3 className="font-heading text-sm font-bold text-[#3E2723]">Work Schedule</h3>
        <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]" data-testid="ws-weekly-hours">{weeklyHours.toFixed(1)} hrs/week</span>
        {canEdit && !editing && (
          <button data-testid="ws-edit-btn" onClick={() => { setDraft(JSON.parse(JSON.stringify(sched))); setEditing(true); }}
            className="ml-auto flex items-center gap-1 rounded-full bg-[#F5F2EB] px-3 py-1 text-xs font-bold text-[#6D4C41] hover:bg-[#EDE7DC]"><Pencil size={11} /> Edit</button>
        )}
      </div>
      {!editing ? (
        <div className="space-y-1.5">
          {DAYS.map(([d, label]) => {
            const e = sched.days?.[d] || {};
            return (
              <div key={d} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-2 text-sm" data-testid={`ws-day-${d}`}>
                <span className="w-24 font-semibold text-[#3E2723]">{label}</span>
                {e.enabled
                  ? <span className="text-[#5D4037] tabular-nums">{e.start} - {e.end}</span>
                  : <span className="rounded-full bg-[#EFEBE0] px-2 py-0.5 text-[10px] font-bold text-[#84675B]">OFF</span>}
              </div>
            );
          })}
          {sched.notes && <div className="text-xs text-[#7B6157] pt-1">{sched.notes}</div>}
        </div>
      ) : (
        <div className="space-y-1.5">
          {DAYS.map(([d, label]) => {
            const e = draft.days[d];
            return (
              <div key={d} className="flex flex-wrap items-center gap-3 rounded-xl border border-[#F1EEE7] px-3 py-2 text-sm">
                <label className="flex w-28 items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid={`ws-day-${d}-toggle`} className="h-4 w-4 accent-[#4E342E]" checked={e.enabled} onChange={(ev) => setDay(d, { enabled: ev.target.checked })} />
                  <span className="font-semibold text-[#3E2723]">{label}</span>
                </label>
                {e.enabled && (
                  <div className="flex items-center gap-2">
                    <Input data-testid={`ws-day-${d}-start`} type="time" className="w-28 py-1" value={e.start} onChange={(ev) => setDay(d, { start: ev.target.value })} />
                    <span className="text-xs text-[#84675B]">to</span>
                    <Input data-testid={`ws-day-${d}-end`} type="time" className="w-28 py-1" value={e.end} onChange={(ev) => setDay(d, { end: ev.target.value })} />
                  </div>
                )}
              </div>
            );
          })}
          <div><Input data-testid="ws-notes-input" placeholder="Notes (e.g., remote on Fridays)" value={draft.notes || ""} onChange={(ev) => setDraft({ ...draft, notes: ev.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-1">
            <Btn variant="ghost" onClick={() => setEditing(false)}>Cancel</Btn>
            <Btn data-testid="ws-save-btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Schedule"}</Btn>
          </div>
        </div>
      )}
    </Card>
  );
}

export function AssignedClientsCard({ userId, canEdit }) {
  const [clients, setClients] = useState(null);
  const [all, setAll] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/users/${userId}/clients`).then((r) => setClients(r.data)).catch(() => setClients([]));
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAssign = async () => {
    setSel([]); setSearch("");
    const [c, u] = await Promise.all([api.get("/clients"), api.get("/users")]);
    setAll(c.data); setUsers(u.data); setOpen(true);
  };
  const clinName = (id) => { const u = users.find((x) => x.id === id); return u ? `${u.first_name} ${u.last_name}` : ""; };
  const candidates = useMemo(() => all
    .filter((c) => c.clinician_id !== userId)
    .filter((c) => `${c.first_name} ${c.last_name}`.toLowerCase().includes(search.toLowerCase())), [all, userId, search]);

  const assign = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${userId}/assign-clients`, { client_ids: sel, action: "assign" });
      toast.success(`${sel.length} client${sel.length === 1 ? "" : "s"} assigned`); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const unassign = async (c) => {
    try {
      await api.post(`/users/${userId}/assign-clients`, { client_ids: [c.id], action: "unassign" });
      toast.success(`${c.first_name} ${c.last_name} unassigned`); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Card className="p-5" data-testid="caseload-card">
      <div className="mb-3 flex items-center gap-2">
        <Users size={15} className="text-[#7B6157]" />
        <h3 className="font-heading text-sm font-bold text-[#3E2723]">Assigned Clients</h3>
        <span className="rounded-full bg-[#F5F2EB] px-2 py-0.5 text-[10px] font-bold text-[#6D4C41]" data-testid="caseload-count">{clients?.length ?? "…"}</span>
        {canEdit && <Btn data-testid="caseload-assign-btn" className="ml-auto px-3 py-1.5 text-xs" onClick={openAssign}><Plus size={12} /> Assign Clients</Btn>}
      </div>
      {!clients ? <div className="h-20 animate-pulse rounded-xl bg-[#EFEBE9]" /> : clients.length === 0 ? <Empty text="No clients assigned" /> : (
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1">
          {clients.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-xl border border-[#F1EEE7] px-4 py-2" data-testid={`caseload-row-${c.id}`}>
              <div className="min-w-0">
                <Link to={`/clients/${c.id}/console`} className="text-sm font-bold text-[#3E2723] hover:underline">{c.first_name} {c.last_name}</Link>
                <div className="text-[11px] text-[#7B6157]">HRN {c.health_record_number || c.mrn || "-"}</div>
              </div>
              <span className="ml-auto"><Badge value={c.status} /></span>
              {canEdit && (
                <button data-testid={`caseload-unassign-${c.id}`} title="Unassign" onClick={() => unassign(c)}
                  className="rounded-full p-1.5 text-[#84675B] hover:bg-[#FFEBEE] hover:text-[#C62828]"><X size={13} /></button>
              )}
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Assign Clients">
        <div className="space-y-3" data-testid="assign-modal">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#84675B]" />
            <Input data-testid="assign-search" className="pl-9" placeholder="Search clients…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-xl border border-[#EFEBE0] p-2">
            {candidates.length === 0 && <div className="py-6 text-center text-xs text-[#84675B]">No matching clients</div>}
            {candidates.map((c) => {
              const cur = c.clinician_id ? clinName(c.clinician_id) : "";
              return (
                <label key={c.id} className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm hover:bg-[#F5F2EB] cursor-pointer">
                  <input type="checkbox" data-testid={`assign-client-${c.id}`} className="h-4 w-4 accent-[#4E342E]"
                    checked={sel.includes(c.id)} onChange={() => setSel((s) => s.includes(c.id) ? s.filter((x) => x !== c.id) : [...s, c.id])} />
                  <span className="font-semibold text-[#3E2723]">{c.first_name} {c.last_name}</span>
                  {cur
                    ? <span className="ml-auto rounded-full bg-[#FFF3E0] px-2 py-0.5 text-[10px] font-bold text-[#B84A00]">with {cur}</span>
                    : <span className="ml-auto text-[10px] text-[#84675B]">unassigned</span>}
                </label>
              );
            })}
          </div>
          <div className="flex justify-end gap-2">
            <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn data-testid="assign-confirm-btn" disabled={sel.length === 0 || busy} onClick={assign}>
              {busy ? "Assigning…" : `Assign ${sel.length || ""} Client${sel.length === 1 ? "" : "s"}`}
            </Btn>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
